export const EDITING_MODE_ROUTES = [
  {
    id: 'style-rendering',
    // 单路由：所有 mode 共用同一个 /vidu-stream/editing，mode 走客户端状态或 ?mode= 深链。
    // href 保留为「该 mode 的深链入口」，供外部（footer、s 页卡、分享链接）跳转进来。
    href: '/vidu-stream/editing',
    navigationKey: 'styleRendering',
  },
  {
    id: 'character-replacement',
    href: '/vidu-stream/editing',
    navigationKey: 'characterReplacement',
  },
  {
    id: 'background-replacement',
    href: '/vidu-stream/editing',
    navigationKey: 'backgroundReplacement',
  },
  {
    id: 'virtual-try-on',
    href: '/vidu-stream/editing',
    navigationKey: 'virtualTryOn',
  },
] as const;

export type EditingMode = (typeof EDITING_MODE_ROUTES)[number]['id'];

/** 侧边栏与输出视图统一使用的产品展示顺序。 */
export const EDITING_MODE_DISPLAY_ORDER: readonly EditingMode[] = [
  'character-replacement',
  'virtual-try-on',
  'style-rendering',
  'background-replacement',
];

export const EDITING_MODE_ROUTE_BY_ID = Object.fromEntries(
  EDITING_MODE_ROUTES.map((entry) => [entry.id, entry]),
) as Record<EditingMode, (typeof EDITING_MODE_ROUTES)[number]>;

export const DEFAULT_EDITING_MODE_ROUTE = EDITING_MODE_ROUTES[0];

export const DEFAULT_EDITING_MODE: EditingMode = DEFAULT_EDITING_MODE_ROUTE.id;

/** 单路由基础路径。所有 mode 的落地页都长这样。 */
export const EDITING_ROUTE = '/vidu-stream/editing';

/** 火山 RTC 版实时编辑页。产品层复用 editing，引擎走火山 Web SDK。 */
export const EDITING_PRO_ROUTE = '/vidu-stream/editing-pro';

export const isEditingWorkspacePathname = (pathname: string | null | undefined): boolean => {
  if (!pathname) return false;
  return pathname === EDITING_ROUTE || pathname === EDITING_PRO_ROUTE || pathname.startsWith(`${EDITING_PRO_ROUTE}/`);
};

export const findEditingModeRoute = (mode: string) => EDITING_MODE_ROUTES.find((entry) => entry.id === mode);

/**
 * 生成某个 mode 的深链地址。
 *
 * 单路由下 mode 不再由 pathname 区分，深链用 `/vidu-stream/editing?mode=<id>` 表达，
 * 供 footer / s 页卡 / 分享链接从外部进入对应 mode。
 */
export const buildEditingModeHref = (mode: EditingMode | string): string => {
  const entry = findEditingModeRoute(mode);
  const id = entry?.id ?? DEFAULT_EDITING_MODE;
  return `${EDITING_ROUTE}?mode=${id}`;
};

/**
 * 从 pathname 推导当前编辑模式。
 *
 * 单路由后绝大多数字段不再走 pathname，这个函数只在极少数仍以 pathname 判断的
 * 场景兜底（返回默认 mode），真正的 mode 状态由客户端 store / query 驱动。
 */
export const findEditingModeRouteByPathname = (_pathname: string) => EDITING_MODE_ROUTES[0];

/** 从 URL searchParams 里解析 mode 参数；缺省或非法时返回默认 mode。 */
export const resolveEditingModeFromSearchParams = (search: Pick<URLSearchParams, 'get'>): EditingMode => {
  const raw = search.get('mode');
  if (!raw) return DEFAULT_EDITING_MODE;
  return findEditingModeRoute(raw)?.id ?? DEFAULT_EDITING_MODE;
};

/**
 * 把当前 mode 同步到地址栏 ?mode=（replaceState，不触发路由导航）。
 * 四个模式都显式保留 mode 参数，并保留其它 query（如 live_id）。仅客户端有效。
 */
export const syncEditingModeToUrl = (mode: EditingMode): void => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('mode', mode);
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
};
